// frontend/src/pages/ConnectionsPage.tsx (v107.0 - Dark Premium Refactor)

import { useState } from 'react';
import { 
    Box, Typography, Alert, Button, Dialog, 
    DialogTitle, DialogContent, IconButton, Paper, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Tooltip, Chip, Switch, Stack,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'; 
import { useSnackbar } from 'notistack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PowerIcon from '@mui/icons-material/Power';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';

import SyncIcon from '@mui/icons-material/Sync';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import FeatureLockedState from '../components/Common/FeatureLockedState';

import { listConnections, testConnection, updateConnectionStatus, createConnection } from '../services/connectionsService';
import { Connection, TestConnectionResponse, SchemaDiscoveryPayload, SchemaDiscoveryResponse, FileType, ConnectionCreatePayload } from '../types';
import ConnectionForm from '../components/Connections/ConnectionForm';
import ConnectionMappingForm from '../components/Connections/ConnectionMappingForm';
import OperationsDetailHeader from '../components/Operations/OperationsDetailHeader';
import EmptyState from '../modules/pricingJourney/components/EmptyState';
import PageSkeleton, { SkeletonTable } from '../modules/pricingJourney/components/Skeletons';

// --- ESTILOS DARK PREMIUM ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    backgroundImage: 'none',
    boxShadow: 'none',
    overflow: 'hidden'
};

const stealthTableSx = {
    '& .MuiTableCell-root': {
        borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
        color: '#cbd5e1',
        py: 2
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        bgcolor: '#0f172a',
        color: '#94a3b8',
        fontWeight: 700,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: 1
    },
    '& .MuiTableRow-hover:hover': {
        bgcolor: 'rgba(255, 255, 255, 0.02) !important'
    }
};

const dialogPaperSx = {
    bgcolor: '#1e293b',
    border: '1px solid #334155',
    color: '#f8fafc',
    borderRadius: 2,
    backgroundImage: 'none'
};

// --- INICIO: ESTADO DEL ASISTENTE RE-ARQUITECTURADO (INTACTO) ---
type WizardStep = 
  | 'closed' 
  | 'credentials' 
  | 'mapping_customers' 
  | 'mapping_products' 
  | 'mapping_transactions';

interface WizardState {
  step: WizardStep;
  payload: SchemaDiscoveryPayload | null;
  schema: SchemaDiscoveryResponse | null;
  mappings: Record<string, any>; 
  connectionName: string;
}

const initialWizardState: WizardState = {
  step: 'closed',
  payload: null,
  schema: null,
  mappings: {},
  connectionName: '',
};
// --- FIN: ESTADO DEL ASISTENTE RE-ARQUITECTURADO ---

const ConnectionsPage = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const isProfileLoading = user && !user.tier_details;
  const canAccessConnections = user?.tier_details?.feature_api_access === true;
  const [wizardState, setWizardState] = useState<WizardState>(initialWizardState);
  const isWizardOpen = wizardState.step !== 'closed';

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: connections, isLoading, isError, error } = useQuery({ queryKey: ['connections'], queryFn: listConnections });

  const testConnectionMutation = useMutation<TestConnectionResponse, Error, string>({
    mutationFn: testConnection,
    onSuccess: (data) => enqueueSnackbar(data.message, { variant: 'success' }),
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      enqueueSnackbar(detail || 'Error desconocido al probar la conexión.', { variant: 'error' });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateConnectionStatus(id, isActive),
    onSuccess: () => {
      enqueueSnackbar('Estado actualizado.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
    // CAMBIO AQUÍ: Quitamos (error: any) y dejamos paréntesis vacíos ()
    onError: () => { 
      enqueueSnackbar('Error al actualizar estado.', { variant: 'error' });
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });

  const createConnectionMutation = useMutation({
    mutationFn: (data: ConnectionCreatePayload) => createConnection(data),
    onSuccess: () => {
      enqueueSnackbar('Conexión creada exitosamente.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      handleCloseWizard();
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      enqueueSnackbar(detail || 'Error al guardar la conexión.', { variant: 'error' });
    },
  });

  const handleStartWizard = () => setWizardState({ ...initialWizardState, step: 'credentials' });
  const handleCloseWizard = () => setWizardState(initialWizardState);

  const handleCredentialsSuccess = ({ payload, schema }: { payload: SchemaDiscoveryPayload; schema: SchemaDiscoveryResponse }) => {
    setWizardState(prev => ({ ...prev, payload, schema, step: 'mapping_customers' }));
  };

  const handleMappingStepSuccess = (resource: FileType, mappingData: any, connectionName: string) => {
    const newMappings = { ...wizardState.mappings, [resource]: mappingData };
    setWizardState(prev => ({ ...prev, mappings: newMappings, connectionName }));

    if (resource === 'customers') {
      setWizardState(prev => ({ ...prev, step: 'mapping_products' }));
    } else if (resource === 'products') {
      setWizardState(prev => ({ ...prev, step: 'mapping_transactions' }));
    } else if (resource === 'transactions') {
      const finalPayload: ConnectionCreatePayload = {
        ...(wizardState.payload as SchemaDiscoveryPayload), 
        connection_name: connectionName,
        mapping: newMappings,
      };
      createConnectionMutation.mutate(finalPayload);
    }
  };

  const handleBack = () => {
    switch (wizardState.step) {
      case 'mapping_transactions': setWizardState(prev => ({ ...prev, step: 'mapping_products' })); break;
      case 'mapping_products': setWizardState(prev => ({ ...prev, step: 'mapping_customers' })); break;
      case 'mapping_customers': setWizardState(prev => ({ ...prev, step: 'credentials' })); break;
      default: handleCloseWizard();
    }
  };

  // --- VISUAL: CHIPS TÁCTICOS ---
  const renderStatusChip = (status: string) => {
    const chipBase = { borderRadius: 1, fontSize: '0.75rem', fontWeight: 600, border: '1px solid' };
    switch (status) {
        case 'IDLE': return <Chip label="Inactiva" size="small" sx={{ ...chipBase, borderColor: '#475569', color: '#94a3b8', bgcolor: 'transparent' }} />;
        case 'SUCCESS': return <Chip label="Sincronizada" size="small" icon={<CheckCircleIcon style={{color: '#4ade80'}}/>} sx={{ ...chipBase, borderColor: 'rgba(74, 222, 128, 0.3)', color: '#4ade80', bgcolor: 'rgba(74, 222, 128, 0.1)' }} />;
        case 'FAILED': return <Chip label="Error Sync" size="small" icon={<ErrorIcon style={{color: '#f87171'}}/>} sx={{ ...chipBase, borderColor: 'rgba(248, 113, 113, 0.3)', color: '#f87171', bgcolor: 'rgba(248, 113, 113, 0.1)' }} />;
        case 'IN_PROGRESS': return <Chip label="Sincronizando..." size="small" icon={<SyncIcon className="spin" style={{color: '#38bdf8'}}/>} sx={{ ...chipBase, borderColor: 'rgba(56, 189, 248, 0.3)', color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)' }} />;
        default: return <Chip label={status} size="small" />;
    }
  };

  const getWizardTitle = () => {
    switch (wizardState.step) {
      case 'credentials': return 'Paso 1: Credenciales';
      case 'mapping_customers': return 'Paso 2: Mapeo Clientes';
      case 'mapping_products': return 'Paso 3: Mapeo Productos';
      case 'mapping_transactions': return 'Paso 4: Mapeo Ventas';
      default: return 'Nueva Conexión';
    }
  };

  const renderWizardStep = () => {
    if (!wizardState.payload || !wizardState.schema) return null;
    const resourceMap: Partial<Record<WizardStep, FileType>> = {
      mapping_customers: 'customers', mapping_products: 'products', mapping_transactions: 'transactions'
    };
    const currentResource = resourceMap[wizardState.step];

    if (currentResource) {
      return (
        <ConnectionMappingForm
          resourceToMap={currentResource}
          connectionPayload={wizardState.payload}
          discoveredSchema={wizardState.schema}
          onSuccess={(mappingData, connectionName) => handleMappingStepSuccess(currentResource, mappingData, connectionName)}
          onBack={handleBack}
          initialConnectionName={wizardState.connectionName}
          existingMapping={wizardState.mappings[currentResource]}
        />
      );
    }
    return null;
  };

  // 3. CONDICIONALES DE RENDERIZADO (ANTES DEL RETURN PRINCIPAL)

  // A. Loading Spinner (Anti-Flicker)
  // PRC-031: estructura al instante en lugar de una rueda.
  if (isAuthLoading || isProfileLoading) {
    return <PageSkeleton eyebrow="Operaciones · Conexiones" title="Conexiones" variant="table" />;
  }

  // B. Estado Bloqueado (Upsell)
  if (!canAccessConnections) {
    return (
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 7 }}>
        <OperationsDetailHeader
          eyebrow="Operaciones · Conexiones"
          title="Conexiones"
          description="Gestiona integraciones externas para automatizar la actualización de datos cuando tu plan lo permita."
        />
        <Box sx={{
            minHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <FeatureLockedState 
              title="Integraciones ERP & SQL"
              description="Conecta tu base de datos Odoo o PostgreSQL directamente para sincronizar ventas y productos en tiempo real."
              features={[
                'Sincronización automática de Maestros',
                'Lectura directa de Transacciones',
                'Soporte para PostgreSQL Nativo',
                'Integración Odoo ERP (JSON-RPC)',
                'Actualización de datos en Tiempo Real'
              ]}
            />
        </Box>
      </Box>
    );
  }


  return (
    <>
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 7 }}>
        <OperationsDetailHeader
          eyebrow="Operaciones · Conexiones"
          title="Conexiones"
          description="Gestiona tus integraciones ERP y bases de datos desde una única superficie."
          action={
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleStartWizard}
              sx={{
                  bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800, px: 2.2,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#0ea5e9' }
              }}
            >
              Nueva conexión
            </Button>
          }
        />

        {isLoading && <SkeletonTable rows={4} rowHeight={56} />}
        {isError && <Alert severity="error" variant="filled" sx={{ mt: 2 }}>Error al cargar las conexiones: {error instanceof Error ? error.message : 'Error desconocido.'}</Alert>}
        
        {connections && connections.length === 0 && !isLoading && (
            // PRC-032: estado vacío común.
            <EmptyState
              icon={<PowerIcon />}
              title="Aún no hay conexiones"
              text="Conecta tu ERP o tu base de datos para actualizar las ventas y los productos sin subir archivos."
              actionLabel="Nueva conexión"
              onAction={handleStartWizard}
            />
        )}

        {connections && connections.length > 0 && (
          <Paper sx={glassCardSx}>
            <TableContainer>
              <Table sx={stealthTableSx}>
                <TableHead><TableRow><TableCell>Estado</TableCell><TableCell>Nombre</TableCell><TableCell>Tipo</TableCell><TableCell>Última sincronización</TableCell><TableCell>Creada</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
                <TableBody>
                  {connections.map((conn: Connection) => (
                    <TableRow key={conn.id}>
                      <TableCell>
                          <Switch 
                            checked={conn.is_active} 
                            onChange={(e) => updateStatusMutation.mutate({ id: conn.id, isActive: e.target.checked })} 
                            disabled={updateStatusMutation.isPending && updateStatusMutation.variables?.id === conn.id}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#38bdf8' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#38bdf8' } }}
                          />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#f8fafc' }}>{conn.connection_name}</TableCell>
                      <TableCell>{conn.connector_type.replace('_', ' ')}</TableCell>
                      <TableCell>{renderStatusChip(conn.last_sync_status)}</TableCell>
                      <TableCell>{format(new Date(conn.created_at), 'dd/MM/yyyy', { locale: es })}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Probar"><IconButton onClick={() => testConnectionMutation.mutate(conn.id)} sx={{ color: '#38bdf8' }}><PowerIcon /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton color="error" disabled><DeleteIcon /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
      
      <Dialog 
        open={isWizardOpen} 
        onClose={handleCloseWizard} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: dialogPaperSx }} // ESTILO DIALOG DARK
      >
        <DialogTitle sx={{ borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>{getWizardTitle()}</Typography>
          <IconButton onClick={handleCloseWizard} sx={{ color: '#94a3b8' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {wizardState.step === 'credentials' && (
            <ConnectionForm onSuccess={handleCredentialsSuccess} onCancel={handleCloseWizard} />
          )}
          {wizardState.step.startsWith('mapping_') && renderWizardStep()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectionsPage;
